/* Popups — <dialog> based, vanilla JS. Rendered by sections/popups.liquid.
 *
 * Open:   <a href="#popup:HANDLE">, [data-popup-open="HANDLE"], URL hash, ThemePopups.open('HANDLE')
 * Close:  [data-popup-close], <a href="#popup:close">, ESC, backdrop click, swipe down (mobile), ThemePopups.close()
 * Auto:   data-trigger="delay|scroll" on the <dialog>, with data-once for once-per-session
 * Events: 'popup:open' / 'popup:close' (bubbling, detail.handle, detail.source)
 */
(function () {
  'use strict';
  if (window.ThemePopups) return;

  var CLOSE_MS = 240;
  var SWIPE_CLOSE_PX = 90;
  var current = null;

  /* ---------- helpers ---------- */
  function escapeAttr(s) {
    return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');
  }
  function byHandle(h) {
    return h ? document.querySelector('dialog.popup[data-popup="' + escapeAttr(h) + '"]') : null;
  }
  function handleFromHref(href) {
    var m = /#popup:([^&?#\s]+)/.exec(href || '');
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }
  // The theme editor can re-render a section while its dialog is open; the old node is then detached.
  function resetIfDetached() {
    if (current && !current.isConnected) {
      current = null;
      document.documentElement.classList.remove('popup-open');
    }
  }
  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }
  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function storageGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function storageSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function emit(d, name, extra) {
    d.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: Object.assign({ handle: d.dataset.popup }, extra || {}) }));
  }

  /* ---------- open / close ---------- */
  function open(target, opts) {
    var d = typeof target === 'string' ? byHandle(target) : target;
    if (!d) return false;
    resetIfDetached();
    if (d.open) {
      // Re-opened during its own close animation: cancel the close and keep it open.
      if (!d.classList.contains('popup--closing')) return false;
      clearTimeout(d._closeTimer);
      d._closeTimer = null;
      d.classList.remove('popup--closing');
      current = d;
      document.documentElement.classList.add('popup-open');
      return true;
    }
    if (current && current !== d) close(current, true);

    d.classList.remove('popup--closing');
    var sheet = d.querySelector('.popup__sheet');
    if (sheet) { sheet.style.transform = ''; sheet.classList.remove('popup__sheet--snap'); }

    try { d.showModal(); } catch (e) { d.setAttribute('open', ''); }
    document.documentElement.classList.add('popup-open');
    current = d;
    emit(d, 'popup:open', { source: (opts && opts.source) || 'api' });
    return true;
  }

  function close(d, immediate) {
    d = d || current;
    if (!d || !d.open) return;

    d.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (e) {} });

    var finish = function () {
      clearTimeout(d._closeTimer);
      d._closeTimer = null;
      if (d.open) d.close(); // fires the native 'close' event → onClosed() does all cleanup
    };

    if (immediate || reducedMotion()) return finish();
    if (d.classList.contains('popup--closing')) return; // already animating out
    d.classList.add('popup--closing');
    d._closeTimer = setTimeout(finish, CLOSE_MS);
  }

  // Single cleanup path for EVERY close: ours, ESC via CloseWatcher, devtools, third-party scripts.
  function onClosed(d) {
    clearTimeout(d._closeTimer);
    d._closeTimer = null;
    d.classList.remove('popup--closing');
    if (current === d) {
      current = null;
      document.documentElement.classList.remove('popup-open');
    }
    // If the page was opened with #popup:HANDLE, drop the hash so a refresh doesn't reopen it.
    if (handleFromHref(location.hash) === d.dataset.popup && history.replaceState) {
      history.replaceState(null, '', location.pathname + location.search);
    }
    emit(d, 'popup:close');
  }

  /* ---------- per-dialog wiring ---------- */
  function setup(d) {
    if (d.dataset.popupReady) return;
    d.dataset.popupReady = '1';

    d.addEventListener('cancel', function (e) { e.preventDefault(); close(d); }); // ESC (animated)
    d.addEventListener('close', function () { onClosed(d); });                    // any close, incl. native/foreign
    d.addEventListener('click', function (e) { if (e.target === d) close(d); });   // backdrop
    d.querySelectorAll('[data-popup-close]').forEach(function (b) {
      b.addEventListener('click', function () { close(d); });
    });

    setupSwipe(d);
    setupAutoTrigger(d);
  }

  function setupSwipe(d) {
    var sheet = d.querySelector('.popup__sheet');
    var scroller = d.querySelector('.popup__scroll');
    if (!sheet) return;
    var startY = 0, dy = 0, dragging = false;

    sheet.addEventListener('touchstart', function (e) {
      if (!isMobile()) return;
      startY = e.touches[0].clientY;
      dy = 0;
      // Only arm the drag when the content is scrolled to the top; otherwise it's a normal scroll.
      dragging = !scroller || scroller.scrollTop === 0;
      sheet.classList.remove('popup__sheet--snap');
    }, { passive: true });

    sheet.addEventListener('touchmove', function (e) {
      if (!dragging || !isMobile()) return;
      dy = e.touches[0].clientY - startY;
      if (dy <= 0 || (scroller && scroller.scrollTop > 0)) {
        // Finger moved up, or content started scrolling: hand the rest of the gesture to native scroll.
        dragging = false;
        dy = 0;
        sheet.style.transform = '';
        return;
      }
      e.preventDefault();
      sheet.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: false });

    var end = function () {
      if (!dragging) return;
      dragging = false;
      sheet.classList.add('popup__sheet--snap');
      if (dy > SWIPE_CLOSE_PX) {
        sheet.style.transform = 'translateY(100%)';
        setTimeout(function () { sheet.style.transform = ''; close(d, true); }, 200);
      } else {
        sheet.style.transform = '';
      }
    };
    sheet.addEventListener('touchend', end);
    sheet.addEventListener('touchcancel', end);
  }

  function setupAutoTrigger(d) {
    var type = d.dataset.trigger;
    if (!type || type === 'none') return;
    if (window.Shopify && Shopify.designMode) return; // never auto-open inside the theme editor

    var key = 'popup:' + d.dataset.popup + ':shown';
    if (d.dataset.once === 'true' && storageGet(key)) return;

    var fire = function () {
      resetIfDetached();
      if (current) return; // something else is open — don't stack
      if (open(d, { source: type })) storageSet(key, '1');
    };

    if (type === 'delay') {
      setTimeout(fire, (parseFloat(d.dataset.delay) || 5) * 1000);
    } else if (type === 'scroll') {
      var pct = parseFloat(d.dataset.scroll) || 50;
      var onScroll = function () {
        var h = document.documentElement;
        var max = h.scrollHeight - h.clientHeight;
        if (max <= 0) return;
        if ((h.scrollTop || window.scrollY) / max * 100 >= pct) {
          window.removeEventListener('scroll', onScroll);
          fire();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* ---------- global wiring ---------- */
  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('a[href*="#popup:"], [data-popup-open]');
    if (!el) return;
    var h = el.getAttribute('data-popup-open') || handleFromHref(el.getAttribute('href'));
    if (h === 'close') { e.preventDefault(); close(); return; } // <a href="#popup:close"> closes the open popup (usable from rich text)
    if (!h || !byHandle(h)) return; // no such popup on this page — let the link behave normally
    e.preventDefault();
    open(h, { source: 'click' });
  });

  function openFromHash() {
    var h = handleFromHref(location.hash);
    if (h) open(h, { source: 'hash' });
  }
  window.addEventListener('hashchange', openFromHash);

  function init(root) {
    (root || document).querySelectorAll('dialog.popup[data-popup]').forEach(setup);
  }
  function boot() {
    init();
    openFromHash();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ---------- theme editor ---------- */
  document.addEventListener('shopify:section:load', function (e) { resetIfDetached(); init(e.target); });
  document.addEventListener('shopify:section:unload', resetIfDetached);
  document.addEventListener('shopify:block:select', function (e) {
    var d = e.target.closest && e.target.closest('dialog.popup');
    if (d) open(d, { source: 'editor' });
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var d = e.target.closest && e.target.closest('dialog.popup');
    if (d) close(d, true);
  });

  /* ---------- public API ---------- */
  window.ThemePopups = {
    open: function (h) { return open(h, { source: 'api' }); },
    close: function () { close(); },
    current: function () { return current ? current.dataset.popup : null; }
  };
})();
